import React, { useState } from 'react';
import { X, Search, Forward, Send, Users, BookOpen } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import ChatModalPortal from './ChatModalPortal';

export function ForwardMessageModal({
  open,
  onClose,
  message,
  conversations = [],
  onForward,
}) {
  const [search, setSearch] = useState('');
  const [sendingTo, setSendingTo] = useState(null);
  const [error, setError] = useState('');

  if (!open || !message) return null;

  const filtered = conversations.filter((c) => {
    const peerName = [c.peer?.first_name, c.peer?.last_name].filter(Boolean).join(' ');
    const title = c.kind === 'notes' ? 'My Notes' : (c.title || peerName || 'Bisedë');
    return title.toLowerCase().includes(search.toLowerCase());
  });

  const handleSelect = async (c) => {
    setSendingTo(c.id);
    setError('');
    try {
      const forwarded = await onForward(c.id, message);
      if (!forwarded) {
        setError('Mesazhi nuk u përcoll. Provoni përsëri.');
        return;
      }
      onClose();
    } catch (forwardError) {
      console.error('Unable to forward chat message:', forwardError);
      setError('Mesazhi nuk u përcoll. Provoni përsëri.');
    } finally {
      setSendingTo(null);
    }
  };

  return (
    <ChatModalPortal>
      <div
      className="fixed inset-0 z-[1000] flex min-h-screen min-w-full items-center justify-center bg-zinc-950/50 p-4 backdrop-blur-sm animate-in fade-in duration-150"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && sendingTo === null) onClose();
      }}
    >
      <div className="w-full max-w-md overflow-hidden rounded-3xl border-0 bg-white shadow-2xl flex flex-col max-h-[85vh] font-sans">
        <div aria-hidden="true" className="flex h-1.5 w-full shrink-0">
          <span className="flex-1 bg-zinc-950" />
          <span className="flex-1 bg-[#DD0000]" />
          <span className="flex-1 bg-[#FFCE00]" />
        </div>
        <div className="flex min-h-0 flex-1 flex-col p-5">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
              <Forward size={18} />
            </div>
            <div>
              <h3 className="text-sm font-bold text-zinc-950">Përcill Mesazhin</h3>
              <p className="text-xs text-zinc-400">Zgjidhni bisedën ku dëshironi ta dërgoni</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
          >
            <X size={18} />
          </button>
        </div>

        {/* Message Preview */}
        <div className="my-3 rounded-2xl border-l-4 border-[#DD0000] bg-amber-50/70 p-3 text-xs text-zinc-700">
          <p className="mb-0.5 font-bold text-zinc-950">Përmbajtja që po përcillni:</p>
          <p className="line-clamp-3 italic">{message.content || '[Skedar]'}</p>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Kërko student, grup ose shënime..."
            className="h-9 rounded-xl pl-9 text-xs"
          />
        </div>
        {error && <p role="alert" className="mb-2 text-xs text-red-600">{error}</p>}

        {/* Conversation list */}
        <div className="flex-1 overflow-y-auto space-y-1.5 custom-scrollbar pr-1 max-h-64">
          {filtered.length === 0 ? (
            <div className="py-8 text-center text-xs text-zinc-400">Nuk u gjet asnjë bisedë.</div>
          ) : (
            filtered.map((c) => {
              const isNotes = c.kind === 'notes';
              const peerName = [c.peer?.first_name, c.peer?.last_name].filter(Boolean).join(' ');
              const title = isNotes ? 'My Notes' : (c.title || peerName || 'Bisedë');

              return (
                <div
                  key={c.id}
                  className="flex items-center justify-between rounded-2xl p-2.5 hover:bg-zinc-50 border border-transparent hover:border-zinc-200 transition"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-xl text-xs font-bold shrink-0 ${
                        isNotes
                          ? 'bg-amber-100 text-amber-800'
                          : c.kind === 'group'
                          ? 'bg-zinc-100 text-zinc-700'
                          : 'bg-gradient-to-tr from-zinc-800 to-zinc-700 text-[#FFCE00]'
                      }`}
                    >
                      {isNotes ? <BookOpen size={18} /> : c.kind === 'group' ? <Users size={18} /> : title.slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-xs font-bold text-zinc-900">{title}</p>
                      <p className="text-[11px] text-zinc-400 truncate">
                        {isNotes ? 'Shënimet e mia personale' : c.kind === 'group' ? 'Grup mësimor' : 'Bisedë direkte'}
                      </p>
                    </div>
                  </div>

                  <Button
                    size="sm"
                    disabled={sendingTo === c.id}
                    onClick={() => handleSelect(c)}
                    className="h-8 rounded-xl bg-zinc-950 px-3 text-xs font-bold text-[#FFCE00] hover:bg-[#DD0000] hover:text-white"
                  >
                    <Send size={12} className="mr-1" />
                    Përcill
                  </Button>
                </div>
              );
            })
          )}
        </div>
        </div>
      </div>
      </div>
    </ChatModalPortal>
  );
}

export default ForwardMessageModal;
